/**
 * Voice session API functions.
 * Communicates with the backend to get signed URLs and report session end.
 */

interface StartSessionResponse {
  success: boolean;
  signedUrl?: string;
  error?: string;
  message?: string;
}

interface EndSessionResponse {
  success: boolean;
  alreadyProcessed?: boolean;
  creditsDeducted?: number;
  message?: string;
  error?: string;
}

interface SaveLeadResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Extract API base URL from the webhook URL.
 * e.g., "http://localhost:3053/api/v1/webhook" → "http://localhost:3053/api/v1"
 */
function getApiBaseUrl(apiUrl: string): string {
  try {
    const url = new URL(apiUrl);
    return url.origin + '/api/v1';
  } catch {
    // Fallback: strip trailing path segment
    return apiUrl.replace(/\/webhook\/?$/, '');
  }
}

/**
 * Request a signed URL from the backend to start a voice session.
 * The backend checks credits and generates a signed URL from ElevenLabs.
 */
export async function startVoiceSessionAPI(
  apiUrl: string,
  uniqueId: string
): Promise<{ signedUrl: string }> {
  const baseUrl = getApiBaseUrl(apiUrl);
  const response = await fetch(`${baseUrl}/voice/session/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uniqueId }),
  });

  const data: StartSessionResponse = await response.json();

  if (!response.ok || !data.success || !data.signedUrl) {
    throw new Error(data.message || data.error || 'Failed to start voice session');
  }

  return { signedUrl: data.signedUrl };
}

interface LiveAvatarTokenResponse {
  success: boolean;
  session_token?: string;
  avatar_id?: string;
  error?: string;
  message?: string;
}

/**
 * Request a LiveAvatar session token from the backend (also runs the upfront credit
 * gate). The backend mints it with the secret LiveAvatar API key (never exposed to the
 * browser). LiveAvatar hosts the ElevenLabs agent conversation for this session.
 */
export async function startLiveAvatarSessionAPI(
  apiUrl: string,
  uniqueId: string
): Promise<{ sessionToken: string; avatarId: string }> {
  const baseUrl = getApiBaseUrl(apiUrl);
  const response = await fetch(`${baseUrl}/voice/liveavatar/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uniqueId }),
  });

  const data: LiveAvatarTokenResponse = await response.json();

  if (!response.ok || !data.success || !data.session_token || !data.avatar_id) {
    throw new Error(data.message || data.error || 'Failed to start avatar session');
  }

  return { sessionToken: data.session_token, avatarId: data.avatar_id };
}

/**
 * Report a finished LiveAvatar session so the backend can deduct credits by duration.
 * Fire-and-forget friendly; idempotent per sessionRef on the backend.
 */
export async function endLiveAvatarSessionAPI(
  apiUrl: string,
  uniqueId: string,
  sessionRef: string,
  durationSecs: number
): Promise<void> {
  const baseUrl = getApiBaseUrl(apiUrl);
  try {
    await fetch(`${baseUrl}/voice/liveavatar/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uniqueId, sessionRef, durationSecs }),
    });
  } catch (err) {
    console.warn('[witzo-voice] Failed to report LiveAvatar session end:', err);
  }
}

export async function saveVoiceLeadAPI(
  apiUrl: string,
  uniqueId: string,
  sessionId: string,
  lead: { name: string; email: string; country: string }
): Promise<SaveLeadResponse> {
  const baseUrl = getApiBaseUrl(apiUrl);
  const response = await fetch(`${baseUrl}/leads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      widget_key: uniqueId,
      session_id: sessionId,
      name: lead.name,
      email: lead.email,
      country: lead.country,
    }),
  });

  const data: SaveLeadResponse = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.message || data.error || 'Failed to save lead details');
  }

  return data;
}

/**
 * Report to the backend that a voice session has ended.
 * The backend will fetch conversation details from ElevenLabs and deduct credits.
 */
export async function endVoiceSessionAPI(
  apiUrl: string,
  uniqueId: string,
  conversationId: string
): Promise<EndSessionResponse | null> {
  const baseUrl = getApiBaseUrl(apiUrl);

  try {
    const response = await fetch(`${baseUrl}/voice/session/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uniqueId, conversationId }),
    });
    const data: EndSessionResponse = await response.json();

    if (!response.ok || !data.success) {
      return null;
    }
    return data;
  } catch (err) {
    console.error('[witzo-voice] Failed to report voice session end:', err);
    return null;
  }
}

/**
 * Fire-and-forget end session using sendBeacon.
 * Works during page unload (refresh, tab close, navigate away).
 * sendBeacon is guaranteed to be queued even if the page is closing.
 */
export function endVoiceSessionBeacon(
  apiUrl: string,
  uniqueId: string,
  conversationId: string
): void {
  const baseUrl = getApiBaseUrl(apiUrl);
  const payload = JSON.stringify({ uniqueId, conversationId });
  const blob = new Blob([payload], { type: 'application/json' });
  const sent = navigator.sendBeacon(`${baseUrl}/voice/session/end`, blob);
}
